import React, { useState, useEffect, useRef } from "react";
import { FaInfoCircle, FaTrashAlt, FaPlus } from "react-icons/fa";
import {
  Paperclip,
  Image,
  SendHorizonal,
  Mic,
  Square,
  Ellipsis,
  Info,
  File,
  Trash2,
  LucideImage,
  Telescope,
} from "lucide-react";
import { FaRegImage } from "react-icons/fa6";
import Gcip from "../../../utils/gcip";
import api from "../../../utils/api";
import { useAuth } from "../../../utils/AuthProvider";
import AudioRecorder from "../../../utils/AudioRecorder";
import { recorderToolkit } from "../utils/recorderHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { countPromptWords } from "../utils/tokenCounter";

const MessageInput = React.memo(
  ({
    token,
    messages,
    userLevel,
    newMessage,
    setNewMessage,
    handleSendMessage,
    handleFileChange,
    docInputRef,
    imageInputRef,
    handleDocUpload,
    handleImageUpload,
    setModalOpen,
    messageWordCount,
    isLoading,
    setIsLoading,
    selectedFiles,
    setSelectedFiles,
    wait,
    setWait,
    runId,
    threadId,
    setThreadId,
    isRecording,
    setIsRecording,
    isFetchingSpeech,
    setIsFetchingSpeech,
    speechToPrompt,
    setSpeechToPrompt,
    eventSourceRef,
    setLinkSearch,
    setDidSearch,
    setMessages,
    botRole,
    isDocumentUploadDisabled = false,
    setBotRole,
    elecCoop,
    darkMode,
    deepResearchLoading = false,
    deepResearchAwaitingClarification = false,
    deepResearchCompleted = false,
    handleDeepResearchSubmit,
    onStartDeepResearch,
  }) => {
    const isDeepResearchThread = botRole === "deep_research";
    const canAttachToDeepResearch =
      isDeepResearchThread && messages.length === 0;
    const [showInfo, setShowInfo] = useState(false);
    // const [selectedFiles, setSelectedFiles] = useState([]);
    const [showTooltip, setShowTooltip] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showExceededWarning, setShowExceededWarning] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const textRef = useRef(null);
    const dropdownRef = useRef(null);

    const isImageFile = (file) =>
      [".png", ".jpg", ".jpeg", ".gif", ".webp"].some((ext) =>
        file.name.toLowerCase().endsWith(ext),
      );

    const images = selectedFiles.filter(isImageFile);
    const documents = selectedFiles.filter((file) => !isImageFile(file));

    const handleInput = () => {
      if (textRef.current) {
        textRef.current.style.height = "auto";
        textRef.current.style.height = textRef.current.scrollHeight + "px";

        setIsExpanded(textRef.current.scrollHeight > 50);
      }
    };

    useEffect(() => {
      handleInput();
    }, []);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setShowDropdown(false);
        }
      };
      if (showDropdown) {
        document.addEventListener("mousedown", handleClickOutside);
      }
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [showDropdown]);

    useEffect(() => {
      if (!isDocumentUploadDisabled) return;

      setSelectedFiles((prev) =>
        prev.filter(
          (file) =>
            ![".pdf", ".docx", ".pptx", ".txt"].some((ext) =>
              file?.name?.toLowerCase().endsWith(ext),
            ),
        ),
      );
    }, [isDocumentUploadDisabled, setSelectedFiles]);

    const onRecordingComplete = async (blob) => {
      setIsRecording(false);
      setIsFetchingSpeech(true);

      const url = URL.createObjectURL(blob);

      try {
        const formData = new FormData();
        formData.append("file", blob, "audio.wav");

        const response = await api.post("/chat/speech", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
        const transcribedText = response.data.text;
        setNewMessage(transcribedText);

        if (speechToPrompt && transcribedText) {
          await handleSendMessage(transcribedText, null, "user", 0);
        }

        setIsFetchingSpeech(false);
      } catch (error) {
        console.log(error);
      }

      //console.log(blob, url);
    };

    const handleInputChange = (e) => {
      setNewMessage(e.target.value);
      textRef.current.style.height = "auto";
      textRef.current.style.height = `${Math.min(
        textRef.current.scrollHeight,
        125,
      )}px`;
    };

    const onFileChange = (event) => {
      const files = Array.from(event.target.files || []);
      if (!files || files.length === 0) return;

      if (isDeepResearchThread) {
        const maxImageSize = 5 * 1024 * 1024;
        const maxDocSize = 10 * 1024 * 1024;
        const maxTotalSize = 25 * 1024 * 1024;

        const incomingImages = files.filter(isImageFile);
        const incomingDocs = files.filter((file) => !isImageFile(file));
        const existingImages = selectedFiles.filter(isImageFile);
        const existingDocs = selectedFiles.filter((file) => !isImageFile(file));

        if (existingImages.length + incomingImages.length > 5) {
          toast.warning(
            "You can attach up to 5 images to a Deep Research question.",
          );
          event.target.value = "";
          return;
        }
        if (existingDocs.length + incomingDocs.length > 5) {
          toast.warning(
            "You can attach up to 5 files to a Deep Research question.",
          );
          event.target.value = "";
          return;
        }
        const oversizedImage = incomingImages.find(
          (file) => file.size > maxImageSize,
        );
        if (oversizedImage) {
          toast.error(`${oversizedImage.name} exceeds the 5MB image limit.`);
          event.target.value = "";
          return;
        }
        const oversizedDoc = incomingDocs.find(
          (file) => file.size > maxDocSize,
        );
        if (oversizedDoc) {
          toast.error(`${oversizedDoc.name} exceeds the 10MB file limit.`);
          event.target.value = "";
          return;
        }
        const combinedSize = [...selectedFiles, ...files].reduce(
          (sum, file) => sum + file.size,
          0,
        );
        if (combinedSize > maxTotalSize) {
          toast.error("Combined attachments cannot exceed 25MB.");
          event.target.value = "";
          return;
        }

        handleFileChange(files);
        event.target.value = "";
        setShowDropdown(false);
        return;
      }

      const hasBlockedDocument =
        isDocumentUploadDisabled &&
        files.some((file) =>
          [".pdf", ".docx", ".pptx", ".txt"].some((ext) =>
            file.name.toLowerCase().endsWith(ext),
          ),
        );

      if (hasBlockedDocument) {
        toast.warning(
          "Document uploads are temporarily disabled for the invoicing bot.",
        );
        event.target.value = "";
        setShowDropdown(false);
        return;
      }
      const maxFileSize = 25 * 1024 * 1024;

      let validFiles = files;
      if (selectedFiles.length + validFiles.length > 10) {
        toast.warning("You can only upload up to 10 files.");
        validFiles = validFiles.slice(0, 10 - selectedFiles.length);
      }

      const totalSize = [...selectedFiles, ...validFiles].reduce(
        (sum, file) => sum + file.size,
        0,
      );
      if (totalSize > maxFileSize) {
        toast.error("Total file size cannot exceed 25MB limit");
        return;
      }
      console.log(validFiles);
      handleFileChange(validFiles);
      event.target.value = "";
      setShowDropdown(false);
    };

    const onSendClick = () => {
      if (isLoading || wait || isRecording || deepResearchLoading) {
        return;
      }

      if (isDeepResearchThread) {
        if (newMessage.trim() === "" || deepResearchCompleted) return;
        handleDeepResearchSubmit(newMessage, selectedFiles);
        setSelectedFiles([]);
        if (docInputRef.current || imageInputRef.current) {
          docInputRef.current.value = null;
          imageInputRef.current.value = null;
        }
        textRef.current.style.height = "20px";
        return;
      }

      const hasText = newMessage.trim() !== "";
      const hasFiles = selectedFiles.length > 0;
      let messageToSend = newMessage;

      if (!hasText && hasFiles) {
        messageToSend =
          selectedFiles.length === 1
            ? "Analyze this file:"
            : "Analyze these files:";
      }

      const promptWordCount = countPromptWords(messageToSend);
      const withinLimit =
        messageWordCount + promptWordCount <= 5000 ||
        userLevel === "premium" ||
        userLevel === "employee";

      if ((hasText || hasFiles) && withinLimit) {
        setNewMessage("");
        console.log("Selected files:", selectedFiles);

        handleSendMessage(
          messageToSend,
          selectedFiles,
          "user",
          promptWordCount,
        );
        setSelectedFiles([]);
        if (docInputRef.current || imageInputRef.current) {
          docInputRef.current.value = null;
          imageInputRef.current.value = null;
        }
        textRef.current.style.height = "20px";
      }
    };

    const handleCancel = async () => {
      try {
        try {
          await api.post(
            `/v2/responses-chat/conversation/${threadId}/stop`,
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
        } finally {
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
          }

          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1] = {
              ...updatedMessages[updatedMessages.length - 1],
              isAnalyzing: false,
            };

            localStorage.setItem(
              `messages_${threadId}`,
              JSON.stringify(updatedMessages),
            );
            return updatedMessages;
          });

          setWait(false);
          setIsLoading(false);
          setDidSearch(false);
          setLinkSearch(false);
        }
      } catch (error) {
        console.error("Error cancelling stream:", error);
        setWait(false);
        setIsLoading(false);
      }
    };

    useEffect(() => {
      setShowExceededWarning(messageWordCount >= 5000);
    }, [messageWordCount]);

    // const hasImageMessage = messages.some(
    //   (msg) => msg?.image_file_id?.length > 0,
    // );

    return (
      <div
        className={`${
          showDropdown ? "pt-2" : selectedFiles.length > 0 ? "pt-1" : "pt-4"
        } absolute bottom-0 left-0 right-0 z-40 bg-transparent px-4 transition-all duration-200 sm:px-8`}>
        {isRecording && recorderToolkit({ isRecording })}

        <div className="flex items-center justify-center w-full">
          {isRecording ? (
            <AudioRecorder
              onRecordingComplete={(blob) => {
                setIsRecording(false);
                onRecordingComplete(blob);
              }}
              setReturn={setIsRecording}
              startButtonText="Start Recording"
              stopButtonText="Stop Recording"
              playButtonText="Play Recording"
              buttonStyles={{ backgroundColor: "green", color: "white" }}
              audioStyles={{ marginBottom: "10px" }}
            />
          ) : (
            <div className="relative w-full max-w-full rounded-lg">
              <div className="w-full max-w-md mx-auto bg-transparent dark:bg-slate-800 lg:max-w-2xl xl:max-w-5xl">
                {isDeepResearchThread && (
                  <div className="flex items-center w-full gap-2 px-3 py-2 mb-2 text-sm font-medium text-orange-800 border border-orange-300 rounded-lg bg-orange-50 dark:border-orange-700 dark:bg-orange-950/60 dark:text-orange-200">
                    <Telescope
                      size={16}
                      strokeWidth={2.5}
                    />
                    {deepResearchCompleted
                      ? "This research is complete. Start a new Deep Research thread to ask another question."
                      : deepResearchAwaitingClarification
                        ? "Deep Research — answer the clarifying question above to continue."
                        : "Deep Research — ask a question to start a report."}
                  </div>
                )}
                {showExceededWarning && userLevel === "free" && (
                  <div className="w-full max-w-full px-3 py-2 mb-3 text-sm font-medium leading-snug text-red-700 break-words border border-red-300 rounded-lg shadow-sm bg-red-50 dark:border-red-800 dark:bg-red-950/70 dark:text-red-200">
                    <span>
                      You've reached the 5000-token limit for your free account.
                      To continue the conversation without interruptions,
                      please{" "}
                    </span>
                    <button
                      type="button"
                      className="font-semibold text-blue-700 underline underline-offset-2 dark:text-blue-300"
                      onClick={() => setModalOpen(true)}>
                      upgrade to our premium plan.
                    </button>
                  </div>
                )}
                <div
                  className={`w-full relative flex flex-col place-self-center p-2
                     bg-slate-200 dark:bg-slate-700 ring-1 focus-within:ring-green-600 ring-slate-400 gap-2 ${
                       selectedFiles.length > 0
                         ? "rounded-lg"
                         : isExpanded
                           ? "rounded-lg"
                           : showDropdown
                             ? "rounded-lg"
                             : "rounded-full"
                     }
                    ${
                      showExceededWarning && userLevel === "free"
                        ? "border-red-500 focus:ring-red-500"
                        : "focus:ring-green-400 dark:focus:ring-green-600"
                    }`}>
                  {showDropdown && !isDeepResearchThread && (
                    <div
                      className={`flex justify-center w-full ${
                        selectedFiles.length === 0 && "pb-2"
                      }`}>
                      <span className="text-xs italic font-semibold text-blue-700 dark:text-blue-300">
                        Please make sure to upload clear invoices/receipts to
                        avoid inaccuracies.
                      </span>
                    </div>
                  )}
                  {showDropdown && (
                    <div
                      className="absolute bottom-0 left-0 z-30 flex flex-col gap-1 px-2 py-2 text-sm -translate-y-10 rounded-md shadow-lg bg-slate-200 dark:bg-slate-700 shadow-black/50"
                      ref={dropdownRef}>
                      <div
                        className="px-2 py-1 rounded-md cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-600"
                        onClick={() => {
                          onStartDeepResearch?.();
                          setShowDropdown(false);
                        }}>
                        <button
                          type="button"
                          className="flex items-center text-gray-600 dark:text-gray-300">
                          <Telescope
                            size={16}
                            strokeWidth={2.5}
                            className="mr-2"
                          />
                          <span className="font-bold">Deep Research</span>
                        </button>
                      </div>
                      {(!isDeepResearchThread || canAttachToDeepResearch) && (
                        <>
                          <div
                            className={`px-2 py-1 rounded-md ${
                              isDocumentUploadDisabled
                                ? "cursor-not-allowed opacity-50"
                                : "cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-600"
                            }`}
                            onClick={() => {
                              if (!isDocumentUploadDisabled) handleDocUpload();
                            }}>
                            <input
                              type="file"
                              ref={docInputRef}
                              className="hidden"
                              onChange={onFileChange}
                              accept={
                                isDeepResearchThread
                                  ? ".pdf,.docx,.pptx,.xlsx,.txt,.md,.csv,.tsv,.json"
                                  : ".pdf,.docx,.pptx,.txt,"
                              }
                              multiple
                            />
                            <button
                              type="button"
                              className="flex items-center text-gray-600 dark:text-gray-300"
                              disabled={
                                isFetchingSpeech || isDocumentUploadDisabled
                              }>
                              <Paperclip
                                size={16}
                                strokeWidth={2.5}
                                className="mr-2"
                              />
                              <span className="font-bold">Add files</span>
                            </button>
                          </div>
                          <div
                            className="px-2 py-1 rounded-md cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-600"
                            onClick={handleImageUpload}>
                            <input
                              type="file"
                              ref={imageInputRef}
                              className="hidden"
                              onChange={onFileChange}
                              accept={
                                isDeepResearchThread
                                  ? ".png,.jpg,.jpeg,.webp"
                                  : ".png,.jpg,.jpeg"
                              }
                              multiple
                            />
                            <button
                              type="button"
                              className="flex items-center text-gray-600 dark:text-gray-300"
                              disabled={isFetchingSpeech}>
                              <Image
                                size={16}
                                strokeWidth={2.5}
                                className="mr-2"
                              />
                              <span className="font-bold">Add photos</span>
                            </button>
                          </div>
                          <div className="flex items-center gap-1 px-1 pt-1 text-blue-700 dark:text-blue-300">
                            <Info
                              size={14}
                              strokeWidth={2.5}
                            />
                            <span className="text-xs font-semibold">
                              {isDeepResearchThread
                                ? "Limit: 5 images, 5 files | 25MB total"
                                : "Limit: 10 images | 25MB"}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {images.length > 0 && (
                    <div className="flex flex-wrap gap-2 px-2">
                      {images.map((file, index) => (
                        <div
                          key={`img-${index}`}
                          className="relative">
                          <p className="flex items-center gap-2">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              className="object-cover rounded-md h-36 w-36"
                            />
                          </p>
                          <div className="absolute p-1 rounded-full bg-slate-100 top-2 right-2">
                            <Trash2
                              size={16}
                              className="text-red-500 cursor-pointer hover:text-red-600"
                              onClick={() => {
                                const newFiles = selectedFiles.filter(
                                  (f) => f !== file,
                                );
                                setSelectedFiles(newFiles);
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {documents.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-1">
                      {documents.map((file, index) => (
                        <div
                          key={`doc-${index}`}
                          className="flex items-center justify-between p-2 text-sm text-gray-800 rounded-lg shadow-inner ring-1 ring-slate-500 dark:text-gray-200 dark:ring-slate-200">
                          <p className="flex items-center gap-1.5 mr-4">
                            <div
                              className={`p-1 rounded-md ${
                                file.name.endsWith(".pdf")
                                  ? "bg-red-400"
                                  : file.name.endsWith(".docx")
                                    ? "bg-blue-400"
                                    : "bg-orange-400"
                              }`}>
                              <File
                                size={18}
                                className="text-gray-800"
                              />
                            </div>
                            {file.name}
                          </p>
                          <Trash2
                            size={20}
                            className="text-red-500 cursor-pointer hover:text-red-600 dark:text-red-400 dark:hover:text-red-500"
                            onClick={() => {
                              const newFiles = selectedFiles.filter(
                                (f) => f !== file,
                              );
                              setSelectedFiles(newFiles);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center w-full gap-2">
                    <div
                      className={`flex items-center rounded-full text-gray-500 dark:text-gray-300 dark:hover:bg-slate-600 hover:bg-slate-300 hover:text-gray-500`}>
                      <button
                        type="button"
                        className="p-2"
                        onClick={() => {
                          setShowDropdown((prev) => !prev);
                        }}
                        disabled={isFetchingSpeech}
                        title="Add file and more"
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}>
                        <FaPlus size={16} />
                      </button>
                    </div>
                    {showTooltip && (
                      <div className="absolute bottom-0 left-0 z-30 px-2 py-1 text-xs text-gray-100 translate-y-4 rounded-md shadow-md bg-slate-700 dark:bg-slate-600 whitespace-nowrap">
                        Add file and more
                      </div>
                    )}
                    <textarea
                      ref={textRef}
                      onInput={handleInput}
                      className="w-full pr-10 text-sm text-gray-900 placeholder-gray-600 transition-all duration-200 bg-transparent border-0 border-none outline-none resize-none focus:outline-none focus:ring-0 focus:border-0 ring-0 dark:text-gray-200 dark:placeholder-gray-300"
                      placeholder={
                        isFetchingSpeech
                          ? ""
                          : isDeepResearchThread
                            ? deepResearchCompleted
                              ? "This research thread is complete."
                              : deepResearchAwaitingClarification
                                ? "Answer the clarifying question..."
                                : "Ask a question to research... 🔭"
                            : "Type your message here... 💬"
                      }
                      value={newMessage}
                      onChange={handleInputChange}
                      onPaste={async (e) => {
                        const items = e.clipboardData.items;
                        let foundImage = false;
                        for (let i = 0; i < items.length; i++) {
                          const item = items[i];
                          if (item.type.startsWith("image/")) {
                            const file = item.getAsFile();
                            if (file) {
                              setSelectedFiles((prev) => [...prev, file]);
                              foundImage = true;
                            }
                          }
                        }
                        if (foundImage) {
                          e.preventDefault();
                          return;
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          e.key === "Enter" &&
                          !e.shiftKey &&
                          !isLoading &&
                          !deepResearchLoading
                        ) {
                          e.preventDefault();
                          onSendClick();
                        }
                      }}
                      rows="1"
                      style={{ maxHeight: "125px", overflow: "auto" }}
                      disabled={
                        (showExceededWarning && userLevel === "free") ||
                        isLoading ||
                        isFetchingSpeech ||
                        deepResearchLoading ||
                        (isDeepResearchThread && deepResearchCompleted)
                      }
                    />
                    <div className="flex items-center rounded-full">
                      <button
                        className={`rounded-full p-1.5 text-gray-500 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-300 ${
                          wait
                            ? "bg-red-400 hover:bg-red-500 shadow text-gray-200 hover:text-gray-300"
                            : isRecording
                              ? ""
                              : (isLoading && !wait) || deepResearchLoading
                                ? "bg-slate-300"
                                : isFetchingSpeech
                                  ? "bg-slate-200"
                                  : "dark:hover:bg-green-800 hover:bg-green-400"
                        }`}
                        onClick={() => {
                          if (wait) {
                            handleCancel();
                          } else if (
                            !isDeepResearchThread &&
                            !newMessage.trim() &&
                            selectedFiles.length === 0
                          ) {
                            setIsRecording(!isRecording);
                          } else {
                            onSendClick();
                          }
                        }}
                        disabled={
                          (showExceededWarning && userLevel === "free") ||
                          isFetchingSpeech ||
                          isLoading ||
                          deepResearchLoading ||
                          (isDeepResearchThread && deepResearchCompleted)
                        }>
                        {wait ? (
                          <Square
                            className="p-0.5"
                            size={20}
                            strokeWidth={2.5}
                            fill="currentColor"
                          />
                        ) : (isLoading && !wait) || deepResearchLoading ? (
                          <div className="flex justify-center rounded-lg shadow-md">
                            <Ellipsis
                              size={20}
                              strokeWidth={2.5}
                            />
                          </div>
                        ) : isRecording ? (
                          <div className="flex justify-center px-2 py-1 bg-green-500 rounded-lg shadow-md sm:px-3 sm:py-2">
                            <span className="text-xs font-medium text-white sm:text-sm">
                              Back to Chat
                            </span>
                          </div>
                        ) : !isDeepResearchThread &&
                          newMessage.trim() === "" &&
                          selectedFiles.length === 0 ? (
                          <Mic
                            size={20}
                            strokeWidth={2.5}
                          />
                        ) : (
                          !deepResearchCompleted && (
                            <SendHorizonal
                              size={20}
                              strokeWidth={2.5}
                            />
                          )
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                {isFetchingSpeech && (
                  <div className="absolute text-gray-400 transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className="animate-spin h-7 w-7"
                    />
                  </div>
                )}
                <div className="flex items-center justify-center py-1 pb-2 text-xs text-gray-600">
                  <FaInfoCircle
                    className="mr-2 text-gray-500 cursor-pointer dark:text-gray-100 hover:text-green-500"
                    onMouseEnter={() => setShowInfo(true)}
                    onMouseLeave={() => setShowInfo(false)}
                  />
                  <p className="text-center dark:text-white">
                    Beth AI might slip up—follow the Prompt Guide or talk to a
                    human for help!{" "}
                  </p>
                  {showInfo && <Gcip />}
                </div>
              </div>
            </div>
          )}
        </div>

        {showErrorModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="p-6 text-center rounded-lg shadow-lg bg-slate-100">
              <h2 className="mb-4 text-2xl font-bold text-red-500">Error</h2>
              <p className="mb-4">
                File size exceeds 25 MB. Please upload a smaller file.
              </p>
              <button
                className="px-4 py-2 text-white bg-red-500 rounded-md hover:bg-red-600"
                onClick={() => setShowErrorModal(false)}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default MessageInput;
